import type { PerspectiveCamera, Scene, WebGLRenderer } from 'three'
import mitt from 'mitt'
import type { TransformControls } from '../control/transformControls'
import type Group from '../group'
import type { ControlsType, UniversalControls } from '../control/UniversalControls'
import { GlobalControl } from './globalControl'
import { EnvManager } from '../scene/envManager'

/**
 * Store management for instance-level configuration
 */
class StoreManagement {
  private store = new Map<string, any>()

  public set(key: string, value: any): void {
    this.store.set(key, value)
  }

  public get<T = any>(key: string): T | undefined {
    return this.store.get(key)
  }

  public has(key: string): boolean {
    return this.store.has(key)
  }

  public delete(key: string): boolean {
    return this.store.delete(key)
  }

  public clear(): void {
    this.store.clear()
  }
}

/**
 * SceneContext - Instance-level state management
 *
 * This class holds all the state that was previously managed globally
 * by GlobalObjectManage. Each SceneControl instance has its own SceneContext,
 * enabling true multi-instance support.
 */
export class SceneContext {
  public id = '0'
  public scene!: Scene
  public domElement!: HTMLElement
  public renderer!: WebGLRenderer
  public orbitControls!: ControlsType
  public triggerClick = false
  public camera!: PerspectiveCamera
  public transformControls: TransformControls[] = []

  public frameCallbacks = new Set<(delta: number) => void>()
  public frameCallbacksAfterRender = new Set<(delta: number) => void>()
  public frameComposerFrameRender = new Set<() => void>()
  public frameSubComposerFrameRender: Set<() => void> = new Set()

  public eventCallbacks: Map<string, Set<((args?: any) => void)>> = new Map()

  public groupCatch: Group[] = []
  public universalControls!: UniversalControls
  public envManager!: EnvManager

  public readonly storeManagement: StoreManagement
  public readonly emitter: ReturnType<typeof mitt>
  public readonly globalControl: GlobalControl

  constructor(customEmitter?: ReturnType<typeof mitt>) {
    this.storeManagement = new StoreManagement()
    this.emitter = customEmitter ?? mitt()
    this.globalControl = new GlobalControl()
  }

  public setId(id: string): void {
    this.id = id
  }

  public addScene(object3d: Scene): void {
    this.scene = object3d
  }

  public setCamera(camera: PerspectiveCamera): void {
    this.camera = camera
  }

  public setDomElement(domElement: HTMLElement): void {
    this.domElement = domElement
  }

  public setRenderer(renderer: WebGLRenderer): void {
    this.renderer = renderer
  }

  public setControls(orbitControls: ControlsType): void {
    this.orbitControls = orbitControls
  }

  public setUniversalControls(universalControls: UniversalControls): void {
    this.universalControls = universalControls
  }

  public setTransformControls(transformControls: TransformControls): void {
    this.transformControls.push(transformControls)
  }

  public setTriggerClickState(state: boolean): void {
    this.triggerClick = state
  }
  
  public setEnvManager(envManager: EnvManager): void {
    this.envManager = envManager
  }

  public addFrameCallback(cb: (delta: number) => void): () => void {
    this.frameCallbacks.add(cb)
    return () => this.frameCallbacks.delete(cb)
  }

  public addSubComposerFrameRender(cb: () => void): () => void {
    this.frameSubComposerFrameRender.add(cb)
    return () => this.frameSubComposerFrameRender.delete(cb)
  }

  public removeSubComposerFrameRender(cb: () => void): void {
    this.frameSubComposerFrameRender.delete(cb)
  }

  public removeFrameCallback(cb: (delta: number) => void): void {
    this.frameCallbacks.delete(cb)
  }

  public addFrameCallbackAfterRender(cb: (delta: number) => void): () => void {
    this.frameCallbacksAfterRender.add(cb)
    return () => this.frameCallbacksAfterRender.delete(cb)
  }

  public addFrameComposerFrameRender(cb: () => void): () => void {
    this.frameComposerFrameRender.add(cb)
    return () => this.frameComposerFrameRender.delete(cb)
  }

  public removeFrameComposerFrameRender(cb: () => void): void {
    this.frameComposerFrameRender.delete(cb)
  }

  public removeFrameCallbackAfterRender(cb: (delta: number) => void): void {
    this.frameCallbacksAfterRender.delete(cb)
  }

  public addCatch(group: Group): void {
    this.groupCatch.push(group)
  }

  /**
   * Cleanup all resources when destroying the scene
   */
  public destroy(): void {
    this.frameCallbacks.clear()
    this.frameCallbacksAfterRender.clear()
    this.frameComposerFrameRender.clear()
    this.frameSubComposerFrameRender.clear()

    this.eventCallbacks.clear()

    this.transformControls = []

    this.groupCatch = []

    this.storeManagement.clear()

    this.emitter.all.clear()
  }
}
